<script setup lang="ts">
import { useStripe } from '~/composables/use-stripe';
import { useStripeApi } from '~/composables/api/use-stripe-api';

const props = withDefaults(
  defineProps<{
    submitLabel?: string;
    disabled?: boolean;
  }>(),
  { submitLabel: 'Save Payment Method', disabled: false },
);

const emit = defineEmits<{
  'payment-method-collected': [paymentMethodId: string];
}>();

const { createSetupIntent } = useStripeApi();
const {
  isLoading,
  isCardComplete,
  cardError,
  mountCardElement,
  confirmSetupIntent,
  destroyCardElement,
} = useStripe();

const cardContainer = ref<HTMLElement | null>(null);
const apiError = ref<string | null>(null);
const isSubmitting = ref(false);

onMounted(() => {
  if (cardContainer.value) {
    mountCardElement(cardContainer.value);
  }
});

onUnmounted(() => {
  destroyCardElement();
});

async function handleSubmit() {
  apiError.value = null;
  isSubmitting.value = true;

  try {
    const setupIntent = await createSetupIntent();
    const paymentMethodId = await confirmSetupIntent(setupIntent.client_secret);
    emit('payment-method-collected', paymentMethodId);
  } catch (e: unknown) {
    // cardError is already set by confirmSetupIntent for Stripe errors.
    // Only set apiError for non-Stripe errors (e.g. network/API failures).
    if (!cardError.value && e instanceof Error) {
      apiError.value = e.message;
    } else if (!cardError.value) {
      apiError.value = 'An unexpected error occurred. Please try again.';
    }
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="payment-method-form">
    <div
      ref="cardContainer"
      class="stripe-card-container"
      data-testid="stripe-card-element"
    />

    <v-alert
      v-if="cardError || apiError"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-3"
    >
      {{ cardError || apiError }}
    </v-alert>

    <v-btn
      color="primary"
      class="mt-4"
      block
      :loading="isLoading || isSubmitting"
      :disabled="!isCardComplete || isLoading || isSubmitting || props.disabled"
      @click="handleSubmit"
    >
      {{ props.submitLabel }}
    </v-btn>
  </div>
</template>

<style scoped>
.stripe-card-container {
  border: 1px solid rgb(var(--v-theme-outline));
  border-radius: var(--arvee-radius-sm);
  padding: 14px 16px;
  transition: border-color 0.2s;
}

.stripe-card-container:focus-within {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}
</style>
